/**
 * Prefix Utilities
 *
 * Loads prefix-to-URI mappings from /prefixes.json and provides
 * helpers for shortening / expanding URIs in the entity view.
 */

import config from '@config/env';

/** Mapping from prefix label to namespace URI, e.g. { "rdf": "http://www.w3.org/1999/02/22-rdf-syntax-ns#" } */
export type PrefixMap = Record<string, string>;

/** Project-specific prefixes that are not in the public prefixes.json */
const PROJECT_PREFIXES: PrefixMap = {
  bnd: config.dataBaseUri,
  bno: config.ontologyBaseUri,
};

let cachedPrefixes: PrefixMap | null = null;

/**
 * Load prefix mappings from /prefixes.json (cached after first call).
 * Merges project-specific prefixes on top.
 */
export async function loadPrefixes(): Promise<PrefixMap> {
  if (cachedPrefixes) return cachedPrefixes;

  try {
    const response = await fetch('/prefixes.json');
    if (!response.ok) {
      console.warn('[prefix.utils] Failed to load prefixes.json, using project prefixes only');
      cachedPrefixes = { ...PROJECT_PREFIXES };
      return cachedPrefixes;
    }
    const json: PrefixMap = await response.json();
    // Project prefixes take precedence
    cachedPrefixes = { ...json, ...PROJECT_PREFIXES };
    return cachedPrefixes;
  } catch (error) {
    console.warn('[prefix.utils] Error loading prefixes.json:', error);
    cachedPrefixes = { ...PROJECT_PREFIXES };
    return cachedPrefixes;
  }
}

/**
 * Shorten a full URI to a prefixed form, e.g.
 *   "http://www.w3.org/1999/02/22-rdf-syntax-ns#type" → "rdf:type"
 *
 * Returns the original URI if no matching prefix is found.
 */
export function shortenUri(uri: string, prefixes: PrefixMap): string {
  // Try longest namespace first so more specific prefixes win
  let bestPrefix = '';
  let bestNamespace = '';

  for (const [prefix, namespace] of Object.entries(prefixes)) {
    if (uri.startsWith(namespace) && namespace.length > bestNamespace.length) {
      bestPrefix = prefix;
      bestNamespace = namespace;
    }
  }

  if (bestNamespace) {
    const localName = uri.slice(bestNamespace.length);
    return `${bestPrefix}:${localName}`;
  }

  return uri;
}

/**
 * Check whether a URI belongs to an internal namespace (data or ontology)
 * that can be dereferenced within this application.
 */
export function isInternalUri(uri: string): boolean {
  return uri.startsWith(config.dataBaseUri) || uri.startsWith(config.ontologyBaseUri);
}

/**
 * Convert an internal URI to the corresponding in-app path.
 * e.g. "https://data.banenor.no/data/foo/bar" → "/data/foo/bar"
 *      "https://data.banenor.no/ontology/SomeClass" → "/ontology/SomeClass"
 *
 * Returns null for external URIs.
 */
export function uriToInternalPath(uri: string): string | null {
  if (uri.startsWith(config.dataBaseUri)) {
    return '/data/' + uri.slice(config.dataBaseUri.length);
  }
  if (uri.startsWith(config.ontologyBaseUri)) {
    return '/ontology/' + uri.slice(config.ontologyBaseUri.length);
  }
  return null;
}

/**
 * Return an in-app path that will dereference the given URI on this site.
 *
 * For URIs that belong to one of our internal namespaces (data / ontology),
 * the dedicated `/data/...` or `/ontology/...` route is used. For any other
 * URI (e.g. `http://www.w3.org/2000/01/rdf-schema#label`) the generic
 * `/dereference?uri=<encoded>` route is used so the resource is described
 * via DESCRIBE against our knowledge graph.
 */
export function uriToDereferencePath(uri: string): string {
  const internal = uriToInternalPath(uri);
  if (internal) return internal;
  return `/dereference?uri=${encodeURIComponent(uri)}`;
}

/**
 * Canonical namespace base – always https://data.banenor.no/ regardless of
 * the environment the app runs on.  Used to reconstruct full URIs from the
 * `/data/*` and `/ontology/*` SPA route paths.
 */
const CANONICAL_BASE = 'https://data.banenor.no/';

/**
 * Reconstruct a full URI from a route path and its type.
 * Always uses the canonical namespace so the describe endpoint accepts it.
 * e.g. pathToUri('foo/bar', 'data') → "https://data.banenor.no/data/foo/bar"
 */
export function pathToUri(path: string, type: 'data' | 'ontology'): string {
  // Route params can contain percent-encoded UTF-8 (for example `%C3%98`).
  // Decode once so describe requests use the canonical IRI form.
  const normalizedPath = (() => {
    try {
      return decodeURI(path);
    } catch {
      // Keep original value if it contains malformed escapes.
      return path;
    }
  })();

  return `${CANONICAL_BASE}${type}/${normalizedPath}`;
}
